import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useDeferredReady } from './use-deferred-ready';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  criteriaType: string;
  criteriaThreshold: number;
  createdAt: string;
}

export interface Achievement {
  id: string;
  userId: string;
  badgeId: string;
  earnedAt: string;
  periodStart: string | null;
  periodEnd: string | null;
  metricValue: number | null;
  badge: Badge;
}

export interface LeaderboardEntry {
  userId: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  role: string;
  totalBadges: number;
  monthBadges: number;
  totalLoads: number;
  totalSaved: number;
  badges: {
    id: string;
    name: string;
    icon: string;
    earnedAt: string;
  }[];
}

export interface LeaderboardData {
  month: string;
  leaderboard: LeaderboardEntry[];
}

export function useBadges() {
  return useQuery({
    queryKey: ['badges'],
    queryFn: async () => {
      const res = await api.get<{ data: Badge[] }>('/badges');
      return res.data.data;
    },
  });
}

export function useMyAchievements() {
  // #1429: non-critical shell data — defer off the cold-load critical path.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: ['badges', 'my-achievements'],
    queryFn: async () => {
      const res = await api.get<{ data: Achievement[] }>('/badges/my-achievements');
      return res.data.data;
    },
    enabled: deferredReady,
  });
}

export function useLeaderboard() {
  return useQuery({
    queryKey: ['badges', 'leaderboard'],
    queryFn: async () => {
      const res = await api.get<{ data: LeaderboardData }>('/badges/leaderboard');
      return res.data.data;
    },
  });
}
